import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Upload, Eye, CheckCircle2, AlertTriangle, Clock, FileQuestion, Loader2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  current: {
    label: "Current",
    icon: CheckCircle2,
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    badge: "bg-emerald-100 text-emerald-700",
    iconColor: "text-emerald-500",
    headerBg: "bg-gradient-to-r from-emerald-400 to-teal-500",
    emoji: "✅",
  },
  expires_soon: {
    label: "Expires Soon",
    icon: Clock,
    bg: "bg-amber-50",
    border: "border-amber-200",
    badge: "bg-amber-100 text-amber-700",
    iconColor: "text-amber-500",
    headerBg: "bg-gradient-to-r from-amber-400 to-orange-400",
    emoji: "⏳",
  },
  expired: {
    label: "Expired",
    icon: AlertTriangle,
    bg: "bg-red-50",
    border: "border-red-200",
    badge: "bg-red-100 text-red-700",
    iconColor: "text-red-500",
    headerBg: "bg-gradient-to-r from-red-400 to-rose-500",
    emoji: "⚠️",
  },
  needs_upload: {
    label: "Needs Upload",
    icon: FileQuestion,
    bg: "bg-slate-50",
    border: "border-slate-200",
    badge: "bg-slate-100 text-slate-600",
    iconColor: "text-slate-400",
    headerBg: "bg-gradient-to-r from-slate-400 to-slate-500",
    emoji: "📄",
  },
};

const formatDate = (str) => {
  if (!str) return null;
  return new Date(str).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const DocumentCard = ({ doc }) => {
  const cfg = STATUS_CONFIG[doc.status] || STATUS_CONFIG.needs_upload;
  const Icon = cfg.icon;
  const needsAction = doc.status === "expired" || doc.status === "needs_upload" || doc.status === "expires_soon";

  return (
    <div className={`rounded-3xl overflow-hidden shadow-md border ${cfg.border}`} data-testid={`parent-doc-${doc.docType}`}>
      {/* Colored header */}
      <div className={`${cfg.headerBg} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <span className="text-xl">{cfg.emoji}</span>
          <p className="text-white font-bold text-sm leading-tight">{doc.label}</p>
        </div>
        <span className="rounded-full bg-white/20 text-white text-xs font-semibold px-3 py-0.5">{cfg.label}</span>
      </div>

      {/* Body */}
      <div className={`${cfg.bg} px-4 py-4 space-y-3`}>
        <p className="text-xs text-slate-500 font-medium">{doc.childName}</p>

        {doc.lastSubmitted && (
          <div className="flex items-center gap-2">
            <p className="text-xs text-slate-500">Submitted:</p>
            <p className="text-xs font-semibold text-slate-700">{formatDate(doc.lastSubmitted)}</p>
          </div>
        )}

        {doc.expirationDate && (
          <div className="flex items-center gap-2">
            <p className="text-xs text-slate-500">Expires:</p>
            <p className={`text-xs font-semibold ${doc.status === "expired" ? "text-red-600" : doc.status === "expires_soon" ? "text-amber-600" : "text-slate-700"}`}>
              {formatDate(doc.expirationDate)}
            </p>
          </div>
        )}

        {!doc.lastSubmitted && !doc.expirationDate && (
          <p className="text-xs text-slate-400 italic">No submission on file</p>
        )}

        {/* Action buttons */}
        <div className="flex gap-2 pt-1 flex-wrap">
          {doc.templateDownloadUrl && (
            <a
              href={doc.templateDownloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-2xl bg-white border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:shadow-md transition"
              data-testid={`parent-doc-download-blank-${doc.docType}`}
            >
              <Download className="h-3.5 w-3.5" /> Download Blank
            </a>
          )}
          {doc.hasFile && doc.viewUrl && (
            <a
              href={doc.viewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 rounded-2xl bg-white border border-slate-200 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:shadow-md transition"
              data-testid={`parent-doc-view-${doc.docType}`}
            >
              <Eye className="h-3.5 w-3.5" /> View Document
            </a>
          )}
          {doc.uploadUrl && needsAction && (
            <a
              href={doc.uploadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-2xl py-2 text-xs font-semibold text-white shadow-sm hover:opacity-90 transition ${cfg.headerBg}`}
              data-testid={`parent-doc-upload-${doc.docType}`}
            >
              <Upload className="h-3.5 w-3.5" />
              {doc.status === "expired" ? "Upload New" : "Upload"}
            </a>
          )}
          {doc.uploadUrl && doc.status === "current" && doc.hasFile && (
            <a
              href={doc.uploadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-2xl bg-white border border-slate-200 px-3 py-2 text-xs text-slate-500 hover:text-slate-700 transition"
              data-testid={`parent-doc-update-${doc.docType}`}
            >
              <Upload className="h-3 w-3" /> Update
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const FILTERS = [
  { key: "all", label: "All", emoji: "📁" },
  { key: "current", label: "Current", emoji: "✅" },
  { key: "expires_soon", label: "Expires Soon", emoji: "⏳" },
  { key: "expired", label: "Expired", emoji: "⚠️" },
  { key: "needs_upload", label: "Needs Upload", emoji: "📄" },
];

export default function ParentDocumentsPage() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/parent-communication/parent/documents`)
      .then((res) => setDocuments(res.data.documents || []))
      .catch(() => toast.error("Unable to load documents"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? documents : documents.filter((d) => d.status === filter);
  const counts = {
    expired: documents.filter((d) => d.status === "expired").length,
    needs_upload: documents.filter((d) => d.status === "needs_upload").length,
    expires_soon: documents.filter((d) => d.status === "expires_soon").length,
  };
  const attentionCount = counts.expired + counts.needs_upload + counts.expires_soon;

  return (
    <div className="min-h-screen px-4 py-6" style={{ background: "radial-gradient(circle at top left, rgba(59,130,246,0.10), transparent 40%), radial-gradient(circle at bottom right, rgba(16,185,129,0.10), transparent 40%), #f6fbfa" }} data-testid="parent-documents-page">
      <div className="mx-auto max-w-lg space-y-5">

        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/parent")} className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white shadow-sm border border-slate-200" data-testid="parent-docs-back-btn">
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Enrollment Documents (PDF)</h1>
            <p className="text-sm text-slate-500">Blank forms to fill out, and records you&apos;ve submitted</p>
          </div>
        </div>

        {/* Attention banner */}
        {attentionCount > 0 && !loading && (
          <div className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 flex items-center gap-3" data-testid="parent-docs-attention-banner">
            <span className="text-2xl">⚠️</span>
            <p className="text-sm font-semibold text-rose-700">
              {attentionCount} document{attentionCount !== 1 ? "s" : ""} need{attentionCount === 1 ? "s" : ""} your attention
            </p>
          </div>
        )}

        {/* Filter pills */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map((f) => {
            const count = f.key !== "all" ? documents.filter((d) => d.status === f.key).length : documents.length;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`flex-shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition whitespace-nowrap ${
                  filter === f.key
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`parent-docs-filter-${f.key}`}
              >
                {f.emoji} {f.label} <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${filter === f.key ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <p className="text-sm text-slate-400">Loading your documents...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="parent-docs-empty">
            <div className="text-5xl mb-4">📁</div>
            <p className="font-semibold text-slate-700">No documents in this category</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((doc) => (
              <DocumentCard key={`${doc.childId}-${doc.docType}`} doc={doc} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
